// =============================================================================
// Monogram — generated avatar (v0.2.36)
// =============================================================================
//
// A colored circle with initials, generated deterministically from a name.
// Used wherever a school or scholarship sponsor needs a visual anchor on a
// card. No external data, no network, never fails — the honest alternative to
// real logos (which College Scorecard doesn't provide and which can't be
// sourced cleanly; see BACKLOG.md).
// =============================================================================

interface Props {
  /** The name to derive initials and color from (school or sponsor name). */
  name: string | null | undefined;
  /** Pixel size of the circle. Default 32. */
  size?: number;
  className?: string;
}

// A palette of pleasant, sufficiently-distinct background colors. The name
// hashes to one of these, so the same name always gets the same color.
const PALETTE = [
  { bg: '#1d4434', fg: '#ffffff' }, // forest
  { bg: '#0891b2', fg: '#ffffff' }, // cyan
  { bg: '#059669', fg: '#ffffff' }, // emerald
  { bg: '#d97706', fg: '#ffffff' }, // amber
  { bg: '#dc2626', fg: '#ffffff' }, // red
  { bg: '#7c3aed', fg: '#ffffff' }, // violet
  { bg: '#db2777', fg: '#ffffff' }, // pink
  { bg: '#2563eb', fg: '#ffffff' }, // blue
  { bg: '#65a30d', fg: '#ffffff' }, // lime
  { bg: '#0d9488', fg: '#ffffff' }, // teal
];

/** Stable string hash -> non-negative integer. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/**
 * Initials from a name. Takes the first letter of up to the first two
 * "significant" words, skipping common filler words. Falls back gracefully.
 */
export function initialsFromName(name: string | null | undefined): string {
  if (!name) return '?';
  const skip = new Set([
    'the', 'of', 'and', 'a', 'an', 'for', 'at', 'in', 'on', '&',
  ]);
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0 && !skip.has(w.toLowerCase()));
  if (words.length === 0) {
    // Name was only filler/punctuation — use the first alphanumeric char.
    const c = name.trim().replace(/[^a-zA-Z0-9]/g, '')[0];
    return c ? c.toUpperCase() : '?';
  }
  if (words.length === 1) {
    // Single word — first two letters (e.g. "Stanford" -> "ST").
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function Monogram({ name, size = 32, className = '' }: Props) {
  const label = (name ?? '').trim();
  const initials = initialsFromName(label);
  const color = PALETTE[hashString(label || '?') % PALETTE.length];
  // Font scales with the circle; 2-letter initials sit comfortably at ~42%.
  const fontSize = Math.round(size * 0.42);

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold leading-none ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: color.bg,
        color: color.fg,
        fontSize,
      }}
      aria-hidden="true"
      title={label || undefined}
    >
      {initials}
    </span>
  );
}
